import {
  afterNextRender,
  Directive,
  ElementRef,
  HostAttributeToken,
  inject,
  NgZone,
  OnDestroy,
  signal,
} from "@angular/core";

import { GlossScrollbar, glossScrollEnhanced } from "./gloss-scrollbar";

/**
 * GlossScroll's overlay scrollbar on the element it's put on, which must be the scroll container
 * itself (overflow auto / scroll on either axis). Nothing about the element changes but its native
 * bar being hidden, so scroll listeners, scrollIntoView and sticky children keep working. On
 * touch-only devices it draws nothing and the OS overlay bar stays.
 */
@Directive({
  selector: "[appGlossScroll]",
  standalone: true,
})
export class GlossScrollDirective implements OnDestroy {
  /**
   * Whether the element can scroll on either axis, kept up to date only with the
   * `appGlossScrollTrackOverflow` attribute (static: it's measured anyway on pointer devices,
   * not on touch).
   */
  readonly overflowing = signal(false);

  private readonly el = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly zone = inject(NgZone);
  private readonly track =
    inject(new HostAttributeToken("appGlossScrollTrackOverflow"), { optional: true }) !== null;
  private scrollbar?: GlossScrollbar;

  constructor() {
    afterNextRender(() => {
      const enhanced = glossScrollEnhanced();
      const track = this.track;
      if (!enhanced && !track) {
        return; // the OS bar, and nothing to report
      }
      // Outside Angular: scroll and pointer handling shouldn't run change detection.
      this.zone.runOutsideAngular(() => {
        this.scrollbar = new GlossScrollbar(this.el.nativeElement, {
          enhanced,
          onOverflow: track
            ? (overflowing) => this.zone.run(() => this.overflowing.set(overflowing))
            : undefined,
        });
      });
    });
  }

  ngOnDestroy(): void {
    this.scrollbar?.destroy();
  }
}
